// Identidade do fornecedor DENTRO do orçamento.
//
// `supplierList[].supplierId` NÃO é o `_id` do fornecedor: quando o mesmo
// fornecedor aparece mais de uma vez na mesma forma de cobrança, o id recebe um
// sufixo para que cada linha tenha identidade própria (visibilidade por etapa,
// serviços, totais e baseValues são todos indexados por esse id).
//
//   <_id>                  → caso normal
//   <_id>|dup|<n>          → mesmo fornecedor repetido na mesma forma de cobrança
//   <_id>-<calcOption>-<n> → fornecedor temporário ("A DEFINIR" / cnpj com `tbd`)
//
// A regra de geração do sufixo estava replicada em três lugares
// (BudgetSupplier, SupplierAutoComplete e agora a transferência de categoria);
// centralizar aqui evita que duas linhas disputem o mesmo id — sintoma: a
// categoria transferida some da tela porque herda a visibilidade da outra linha.

type SupplierListEntry = {
  supplierId: string;
  calcOption: string;
};

/** Extrai o `_id` real do fornecedor a partir do id interno do orçamento. */
export function parseSupplierId(supplierId: string): string {
  if (!supplierId) return "";
  if (supplierId.includes("|")) return supplierId.split("|")[0];
  const parts = supplierId.split("-");
  return parts.length > 1 ? parts[0] : supplierId;
}

/**
 * "Fornecedor a definir": o único fornecedor que pode aparecer repetido no
 * mesmo bloco de propósito — cada linha é um espaço reservado para um
 * fornecedor real diferente. Mesma heurística usada no `SupplierAutoComplete`.
 */
export function isTemporarySupplierOption(
  option:
    | {
        name?: string;
        cnpj_cpf?: string;
      }
    | null
    | undefined
): boolean {
  if (!option) return false;
  return (
    !!option.cnpj_cpf?.includes("tbd") ||
    !!option.name?.toUpperCase().includes("A DEFINIR")
  );
}

/**
 * Id interno a usar ao adicionar `realSupplierId` na forma de cobrança
 * informada. Devolve o `_id` puro quando não há colisão; caso contrário,
 * sufixa com o menor índice livre.
 *
 * O SUFIXO NÃO É O MESMO nos dois casos, e isso é regra de negócio, não
 * estética:
 *
 *   - fornecedor comum       → `<id>|dup|<n>`, que a tela sinaliza como
 *                              duplicidade (o usuário provavelmente errou);
 *   - "fornecedor a definir" → `<id>-<calcOption>-<n>`, isento do aviso
 *                              (ver `sameSupplierOccurrences` em BudgetSupplier,
 *                              que ignora ids com `-`), porque repetir esse
 *                              fornecedor é justamente o uso esperado.
 *
 * Usar `|dup|` para o "a definir" fazia a linha nova nascer acusada de
 * duplicidade indevidamente.
 *
 * A comparação leva em conta TODAS as entradas da forma de cobrança, inclusive
 * as ocultas na etapa atual (`service.versions`): a `supplierList` não é
 * versionada, então uma entrada invisível na tela continua ocupando o id.
 */
export function resolveNewSupplierId(
  realSupplierId: string,
  calcOption: string,
  supplierList: SupplierListEntry[] | undefined,
  options: { isTemporary?: boolean } = {}
): string {
  if (!realSupplierId) return realSupplierId;

  const sameCalcOption = (supplierList ?? []).filter(
    (el) => el.calcOption === calcOption
  );
  const usedIds = new Set(sameCalcOption.map((el) => el.supplierId));

  // O "a definir" sempre nasce sufixado, mesmo sendo o primeiro do bloco —
  // é o que o `SupplierAutoComplete` já faz ao escolhê-lo manualmente.
  if (options.isTemporary) {
    let index = 0;
    while (usedIds.has(`${realSupplierId}-${calcOption}-${index}`)) index += 1;
    return `${realSupplierId}-${calcOption}-${index}`;
  }

  const collides = sameCalcOption.some(
    (el) => parseSupplierId(el.supplierId) === realSupplierId
  );
  if (!collides) return realSupplierId;

  let suffix = 1;
  while (usedIds.has(`${realSupplierId}|dup|${suffix}`)) suffix += 1;
  return `${realSupplierId}|dup|${suffix}`;
}

/**
 * Id interno a usar quando o fornecedor é escolhido NO CAMPO de seleção.
 *
 * Difere de `resolveNewSupplierId` em dois pontos, e os dois vêm de o campo
 * estar dentro de uma linha que já tem um id:
 *
 *   - `currentSupplierId` (o id da própria linha) não conta como colisão —
 *     senão trocar o fornecedor de uma linha e voltar atrás sufixaria o id;
 *   - o sufixo `|dup|` usa o menor índice livre, igual ao
 *     `resolveNewSupplierId`. Contar ocorrências devolvia `|dup|N` já
 *     ocupado (ex.: só existia `|dup|1`) e a linha nova herdava a
 *     visibilidade da outra — o fornecedor recém-escolhido sumia.
 *
 * `reuseExisting` inverte a decisão do fornecedor comum: devolve o `_id` puro
 * mesmo com ocorrências, para quem quer REUSAR a linha existente em vez de
 * criar uma segunda. É o caso do formulário do agrupamento — sufixar ali criava
 * o fornecedor duplicado relatado pelo QA. O "a definir" segue sufixado: ali
 * cada linha é mesmo um espaço reservado diferente.
 */
export function resolveSelectedSupplierId(params: {
  realSupplierId: string;
  calcOption: string;
  supplierList: SupplierListEntry[] | undefined;
  currentSupplierId: string;
  isTemporary: boolean;
  reuseExisting?: boolean;
}): string {
  const {
    realSupplierId,
    calcOption,
    supplierList,
    currentSupplierId,
    isTemporary,
    reuseExisting,
  } = params;

  if (!realSupplierId) return "";

  const others = (supplierList ?? []).filter(
    (el) =>
      el.supplierId !== currentSupplierId &&
      el.calcOption === calcOption &&
      parseSupplierId(el.supplierId) === realSupplierId
  );

  const usedIds = new Set(
    (supplierList ?? [])
      .filter(
        (el) =>
          el.calcOption === calcOption && el.supplierId !== currentSupplierId
      )
      .map((el) => el.supplierId)
  );

  if (isTemporary) {
    let index = 0;
    while (usedIds.has(`${realSupplierId}-${calcOption}-${index}`)) index += 1;
    return `${realSupplierId}-${calcOption}-${index}`;
  }

  if (others.length > 0 && !reuseExisting) {
    let suffix = 1;
    while (usedIds.has(`${realSupplierId}|dup|${suffix}`)) suffix += 1;
    return `${realSupplierId}|dup|${suffix}`;
  }

  return realSupplierId;
}
